/**
 * Purpose: Custom hook for fetching reservations data from the owner API.
 * 
 * High-level Workflow:
 * 1. Validates listingId (must be numeric and > 0)
 * 2. Converts reporting period dates to API date range format (YYYY-MM-DD)
 * 3. Validates date range (start < end, valid dates)
 * 4. Fetches reservations from API with listingId and date range
 * 5. Handles both wrapped (response.data.items) and unwrapped (response.items) response formats
 * 6. Manages loading, error, reservations, and metadata state
 * 7. Returns state values for consumption by components
 * 
 * Assumptions & Invariants:
 * - listingId must be a number or numeric string (Hostaway listing_id format)
 * - rangeStart and rangeEnd are Date objects from ReportingPeriodContext
 * - API returns owner-safe data (no guest contact details, no admin-only fields)
 * - API response may be wrapped (response.data.items) or unwrapped (response.items)
 * - API response includes metadata for staleness tracking
 * - All error handling preserves user-facing error messages
 * 
 * Dependencies:
 * Internal:
 *   - services/ownerApi.js - getReservations API function
 *   - utils/reservationsDateUtils.js - getDateRangeFromReportingPeriod utility
 * External:
 *   - react - React hooks (useState, useEffect)
 * 
 * Version: 1.1.0
 * Created: 2026-01-26
 * Change Log:
 * - 1.1.0 (Phase 1): Added AbortController + mount guard for reliability. AbortController cancels
 *   in-flight requests on unmount or dependency change. Mount guard prevents setState after unmount.
 *   AbortError handled silently. No behavior changes to displayed values.
 * - 1.0.0: Initial extraction from ReservationsPage.jsx (Phase 2)
 */

import { useState, useEffect } from 'react';
import { getReservations } from '../services/ownerApi.js';
import { getDateRangeFromReportingPeriod } from '../utils/reservationsDateUtils.js';

/**
 * Custom hook for fetching reservations data
 * 
 * Fetches reservations when listingId or date range changes.
 * Handles validation, error states, and response format variations.
 * 
 * @param {number|string|null} listingId - Property listing ID (must be numeric)
 * @param {Date|null} rangeStart - Start date from ReportingPeriodContext
 * @param {Date|null} rangeEnd - End date from ReportingPeriodContext
 * @param {boolean} debug - Debug flag for logging (optional, defaults to false)
 * @returns {Object} - { reservations, metadata, loading, error }
 */
export function useReservationsData(listingId, rangeStart, rangeEnd, debug = false) {
  const [reservations, setReservations] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [aggregates, setAggregates] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Phase 1: Fetch reservations when listingId or date range changes
  // Added AbortController + mount guard for reliability
  useEffect(() => {
    const abortController = new AbortController();
    let isActive = true;

    const fetchReservations = async () => {
      // Canonical model: use listing_id only (number), never prop-* or UUID
      if (!listingId) {
        if (isActive) {
          setReservations([]);
          setMetadata(null);
          setAggregates(null);
          setLoading(false);
        }
        return;
      }

      // Ensure listing_id is numeric (Hostaway listing_id is numeric)
      const listingIdNum = typeof listingId === 'string' ? parseInt(listingId, 10) : listingId;
      if (isNaN(listingIdNum) || listingIdNum <= 0) {
        console.warn('[useReservationsData] Invalid listing_id:', listingId);
        if (isActive) {
          setError('Invalid listing ID');
          setReservations([]);
          setMetadata(null);
          setAggregates(null);
          setLoading(false);
        }
        return;
      }

      // Get date range from reporting period
      const dateRange = getDateRangeFromReportingPeriod(rangeStart, rangeEnd, debug);
      
      // Validate date range
      const startDateObj = new Date(dateRange.startDate);
      const endDateObj = new Date(dateRange.endDate);
      if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime()) || startDateObj >= endDateObj) {
        console.warn('[useReservationsData] Invalid date range:', dateRange);
        if (isActive) {
          setError('Invalid date range');
          setReservations([]);
          setMetadata(null);
          setAggregates(null);
          setLoading(false);
        }
        return;
      }

      if (isActive) {
        setLoading(true);
        setError(null);
      }

      try {
        if (debug) {
          console.log('[OWNER_SELECT][API] Reservations fetch triggered:', {
            listingId,
            listingIdNum,
            dateRange,
            timestamp: new Date().toISOString(),
          });
        }
        
        const response = await getReservations({
          listingId: listingIdNum,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          signal: abortController.signal,
        });

        // Phase 1: Only update state if component is still mounted and request not aborted
        if (isActive && !abortController.signal.aborted) {
          // Extract items from response (handle both response.items and response.data.items)
          const items = response.items || response.data?.items || [];
          // Extract aggregates (authoritative totals; same filters as list, independent of pagination)
          const responseAggregates = response.aggregates || response.data?.aggregates || null;
          // Extract metadata
          const responseMetadata = response.metadata || response.data?.metadata || null;
          
          if (debug) {
            console.log('[OWNER_SELECT] [useReservationsData] Fetched reservations:', {
              count: items.length,
              listingId: listingIdNum,
              hasAggregates: !!responseAggregates,
            });
          }
          
          setReservations(items);
          setMetadata(responseMetadata);
          setAggregates(responseAggregates);
        }
      } catch (err) {
        // Phase 1: Handle AbortError silently (no user-visible error, no console.error)
        if (err.name === 'AbortError' || abortController.signal.aborted) {
          return; // Silent abort, component unmounted or dependency changed
        }
        
        if (isActive) {
          console.error('[useReservationsData] Error fetching reservations:', err);
          setError(err.message || 'Failed to fetch reservations');
          setReservations([]);
          setMetadata(null);
          setAggregates(null);
        }
      } finally {
        if (isActive) {
          setLoading(false);
        }
      }
    };

    fetchReservations();

    // Phase 1: Cleanup - abort request and mark as inactive on unmount/dependency change
    return () => {
      isActive = false;
      abortController.abort();
    };
  }, [listingId, rangeStart, rangeEnd, debug]);

  return {
    reservations,
    metadata,
    aggregates,
    loading,
    error,
  };
}
